import { VaultRole, type Vault, type VaultInput, type VaultMemberToWrite } from '@/modules/api/codegen';
import type { GroupWithPermissions } from '@/modules/groups';
import type { WhitelistUser } from '@/modules/whitelist';
import type { EntityWithPermissions } from '@/types';

import type { VaultFormValues } from '../schemas';
import { permissionsToVaultRole, vaultRoleToPermissions } from './vaultRole';

const toMember = ({ id, accessPermissions }: EntityWithPermissions): VaultMemberToWrite => ({
    id,
    role: permissionsToVaultRole(accessPermissions),
});

/**
 * Turns a vault into the record-sharing shape the reused member rows speak. Both directions live
 * together because they must stay each other's inverse — see the round-trip test.
 */
export const vaultToFormValues = (vault?: Vault): VaultFormValues => ({
    name: vault?.name ?? '',
    // A member the directory could not resolve arrives without a name or an email. It still keeps its
    // row: submitting replaces the member list wholesale, so dropping it here would delete it from the
    // vault on the next save.
    groups: (vault?.groups ?? []).map<GroupWithPermissions>(({ id, name, email, role }) => ({
        id,
        name,
        email,
        accessPermissions: vaultRoleToPermissions(role ?? VaultRole.viewer),
    })),
    users: (vault?.users ?? []).map<WhitelistUser>(({ id, name, email, role }) => ({
        id,
        name: name ?? '',
        email: email ?? '',
        accessPermissions: vaultRoleToPermissions(role ?? VaultRole.viewer),
    })),
});

export const vaultFormValuesToInput = ({ name, groups, users }: VaultFormValues): VaultInput => ({
    name: name.trim(),
    groups: groups.map(toMember),
    users: users.map(toMember),
});
